import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  loadAppointment,
  patchAppointment,
  rescheduleAppointment,
} from 'actions/appointments';
import Button from 'atoms/button';
import useQuery from 'hooks/useQuery';
import {
  refreshAppointments,
  trackAppointmentAction,
} from 'organisms/Appointments/AppointmentActions/util';
import RescheduleDrawerLoader from 'organisms/Loaders/RescheduleDrawerLoader';
import {
  appointmentFailure,
  appointmentRequest,
  appointmentSuccess,
} from 'store/reducers/appointment';
import {
  closeDrawer,
  requestFulfilled,
  requestOngoing,
} from 'store/reducers/ui';

import GroomerInfo from '../GroomerInfo';
import PetServiceInfo from '../PetServiceInfo';
import RescheduleStatusModal from './RescheduleStatusModal';
import TimeSlotsSelector from './TimeSlotsSelector';

export default function RescheduleDrawer() {
  const dispatch = useDispatch();
  const location = useLocation();
  const query = useQuery();
  const { uuid: appointmentUuid } = useSelector((state) => state.ui.drawer);
  const appointment = useSelector((state) => state.appointment);
  const {
    uuid,
    animal,
    location: appointmentLocation,
    locationUser,
    service,
    note,
  } = appointment;
  const [suggestedSlots, setSuggestedSlots] = useState(1);
  const [suggestedTimes, setSuggestedTimes] = useState({
    0: {},
    1: {},
    2: {},
  });
  const [formError, setFormError] = useState(false);
  const [isRescheduleStatusModalOpen, setIsRescheduleStatusModalOpen] =
    useState(false);
  const [dateTimes, setDateTimes] = useState(null);

  useEffect(() => {
    dispatch(
      loadAppointment({
        apuid: appointmentUuid,
        actions: [appointmentRequest, appointmentSuccess, appointmentFailure],
      })
    );
  }, []);

  useEffect(() => setFormError(false), [suggestedTimes]);

  const onMultipleSlots = () => setSuggestedSlots(3);

  const suggestedDispatchSuccess = ({ success, data, suggested }) => {
    if (!success) return;
    trackAppointmentAction('Reschedule');
    refreshAppointments({
      location,
      query,
      dispatch,
      auid: appointment?.animal?.uuid,
    });
    setDateTimes({
      requested: suggested,
      received: data?.data?.suggested?.[0]?.times,
    });
    setIsRescheduleStatusModalOpen(true);
  };

  const onSendSuggestedTimes = () => {
    const suggested = [];
    Object.entries(suggestedTimes).forEach(([id, data]) => {
      if (data.date && data.timeStart) {
        suggested.push({
          date: data.date,
          times: data.timeStart,
        });
      }
      return null;
    });
    if (suggested.length < 1) return setFormError(true);

    if (!!appointment && !appointment.isLoading) {
      dispatch(requestOngoing());
      if (suggested.length === 1) {
        dispatch(
          patchAppointment(uuid, {
            uuid: {
              animal: animal?.uuid,
              location: appointmentLocation?.uuid,
              location_user: locationUser?.uuid,
              service: service?.uuid,
            },
            date_start: `${suggested[0].date}T${suggested[0].times[0]}`,
            note,
          })
        ).then(({ success, data }) => {
          suggestedDispatchSuccess({ success, data, suggested });
        });
      } else {
        dispatch(
          rescheduleAppointment({
            appointmentId: appointment.uuid,
            body: { suggested },
          })
        ).then(({ success, data }) => {
          suggestedDispatchSuccess({ success, data, suggested });
        });
      }
      dispatch(requestFulfilled());
    }
  };

  if (appointment?.isLoading)
    return (
      <div className="reschedule-drawer">
        <RescheduleDrawerLoader />
      </div>
    );

  return (
    <div className="reschedule-drawer flex-column align-end">
      <div className="w-100">
        <div className="flex-row justify-between mb-10">
          <h2 className="f26">Reschedule</h2>
        </div>
        <p className="f16 mb-20">
          Select a day and time, and we’ll email the client confirming their new
          appointment.
        </p>
        <GroomerInfo />
        <PetServiceInfo />
        <TimeSlotsSelector
          groomer={locationUser?.uuid}
          luid={appointmentLocation?.uuid}
          numberOfSlots={suggestedSlots}
          selectedTimeSlots={suggestedTimes}
          setSelectedTimeSlots={setSuggestedTimes}
        />
        {suggestedSlots < 3 ? (
          <Button
            className="mb-20"
            variant="link"
            size="small"
            onClick={onMultipleSlots}
            text="Suggest multiple times"
          />
        ) : (
          <p className="f12 italic c-secondary-purple mb-20">
            You can suggest up to 3 times for a client to choose from. The
            client will need to respond before the appointment is confirmed.
          </p>
        )}
        <Button
          size="medium"
          onClick={onSendSuggestedTimes}
          text={suggestedSlots === 1 ? 'Confirm' : 'Send suggested times'}
        />
        {formError && (
          <p className="f12 c-red mt-20">
            At least one suggested time is required.
          </p>
        )}
      </div>
      {isRescheduleStatusModalOpen && (
        <RescheduleStatusModal
          isOpen={isRescheduleStatusModalOpen}
          setIsOpen={setIsRescheduleStatusModalOpen}
          onClose={() => {
            setDateTimes(null);
            dispatch(closeDrawer());
          }}
          dateTimes={dateTimes}
          petName={appointment?.animal?.name}
        />
      )}
    </div>
  );
}
